import React, { useEffect, useRef, useState } from 'react';
import { Col } from 'react-bootstrap';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import { useSelect } from '../../modules/contexts/Select/useSelect';
import httpClient, { isHttpError } from '../../services/httpClient';
import { getAllFiscaisPorEntidade } from '../../services/tabelasCompartilhadas/fiscalAquisicaoService';
import { useListUserServidores } from '../../services/usuario/userService';
import theme from '../../theme';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
} from '../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../utils/swal';
import BarraInferiorBotoes from '../BarraInferiorBotoes';
import BoxConteudo from '../BoxConteudo';
import CustomButton from '../CustomButton';
import CustomCol from '../CustomCol';
import CustomInputDate, {
  CustomInputDateRef,
} from '../CustomInputs/CustomInputDate';
import CustomInputLink from '../CustomInputs/CustomInputLink';
import CustomInputText from '../CustomInputs/CustomInputText';
import CustomOffcanvas from '../CustomOffcanvas';
import CustomRow from '../CustomRow';
import CustomSpan from '../CustomSpan';
import {
  headersFiscalContrato,
  valoresExibidosFiscalContrato,
} from './auxiliarFiscalContrato';
import Select from '../InputNew/Select';
import Log from '../Log/Log';
import Tabela from '../Tabela';
import WorkspaceBotoes from '../WorkspaceBotoes';
import WorkspaceFicha from '../WorkspaceFicha';
import BolinhaTabelaStatus from '../bolinhaTabelaStatus';

interface Props {
  tab: string;
  id_tab: string;
}

type FormFiscalType = {
  id: number;
  usuarioId: number;
  fiscalId: number;
  tabId: number;
  tipoFiscal: number;
  dataInicio: string;
  dataTermino: string;
  documentoSei: string;
  status: string;
  link: string;
  observacoes: string;
};

const FiscalContratoComponente: React.FC<Props> = ({ tab, id_tab }) => {
  const user = getUser();
  const userID = user.sub;
  const dataTerminoRef = useRef<CustomInputDateRef>(null);

  //////STATES
  const [showModal, setShowModal] = useState(false);
  const [logData, setLogData] = useState<any>();
  const [fiscalId, setFiscalId] = useState('');

  const usuariosOptions = useListUserServidores();
  const [updating, setUpdating] = useState(false);
  const [statusFiscalStyle, setStatusFiscalStyle] =
    useState<React.CSSProperties>({});
  const [statusFiscal, setStatusFiscal] = useState(true);
  const [data, setData] = useState<any[]>();
  const [isUrlValid, setIsUrlValid] = useState(true);
  const [formFiscal, setFormFiscal] = useState<FormFiscalType>({
    id: 0,
    fiscalId: 0,
    tipoFiscal: 0,
    dataInicio: '',
    dataTermino: '',
    status: '',
    documentoSei: '',
    link: '',
    observacoes: '',
    tabId: parseInt(id_tab, 10),
    usuarioId: userID,
  });

  //////OUTROS
  const statusOption = [
    { value: 0, label: 'Não informado' },
    { value: 1, label: 'Ativo' },
    { value: 2, label: 'Inativo' },
  ];

  const { getSelectDominioPai } = useSelect();
  const tipoFiscalOptions = getSelectDominioPai(76);

  //////FETCH DATA
  const fetchData = async () => {
    try {
      const fiscais = await getAllFiscaisPorEntidade(tab, Number(id_tab));
      setData(fiscais);
    } catch (error) {
      console.error('Erro ao obter dados', error);
    }
  };

  //////USE EFFECT
  useEffect(() => {
    const verde = '#a5faa5';
    const vermelho = '#ffb4b4';

    const status = Number(formFiscal.status);

    const newStatusStyle = {
      backgroundColor: status === 1 ? verde : vermelho,
    };
    setStatusFiscalStyle(newStatusStyle);

    const deveSerReadonly = status === 1;
    setStatusFiscal(deveSerReadonly);

    if (deveSerReadonly) {
      setFormFiscal((prev) => ({
        ...prev,
        dataTermino: '',
      }));
    }
  }, [formFiscal.status]);

  useEffect(() => {
    fetchData();
  }, []);

  //////TABLE E OFFCANVAS
  const tableData =
    data
      ?.slice() // faz uma cópia para não mutar o array original
      .sort(
        (a, b) =>
          new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime(),
      )
      .map((item) => {
        const tipoFiscalOpt = tipoFiscalOptions.find(
          (o) => Number(o.value) === Number(item.tipoFiscal),
        ) ?? { value: 0, label: 'Não informado' };

        const tipoFiscalLabel = tipoFiscalOpt.label;
        const tipoFiscalLetra = tipoFiscalLabel === 'Titular' ? 'T' : 'S';
        const backgroundColor =
          tipoFiscalLabel === 'Titular'
            ? theme.colors.azulgov
            : theme.colors.amarelogov;
        const fontColor = tipoFiscalLabel === 'Titular' ? 'white' : 'black';

        return {
          id: item.id,
          status: <BolinhaTabelaStatus status={item?.status ?? 'N'} />,
          tipoFiscal: (
            <div style={{ textAlign: 'left', justifySelf: 'flex-start' }}>
              <span
                style={{
                  backgroundColor: backgroundColor,
                  borderRadius: '7px',
                  padding: '4px',
                  textAlign: 'center',
                  marginRight: '5px',
                  color: fontColor,
                  display: 'inline-block',
                  minWidth: '30px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                }}
              >
                {tipoFiscalLetra}
              </span>
              {tipoFiscalLabel}
            </div>
          ),
          dataInicio: formatDateSimp(item.dataInicio),
          dataTermino: formatDateSimp(item.dataTermino),
          nomeFiscal: item.fiscal.nomeCompleto,
        };
      }) || [];

  //////FUNÇÕES HANDLE
  const handleClearForm = () => {
    setFormFiscal({
      id: 0,
      fiscalId: 0,
      tipoFiscal: 0,
      dataInicio: '',
      dataTermino: '',
      status: '',
      documentoSei: '',
      link: '',
      observacoes: '',
      tabId: parseInt(id_tab, 10),
      usuarioId: userID,
    });
    setLogData({
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleShowModal = () => setShowModal((prev) => !prev);

  const handleClickInserirNovo = () => {
    handleClearForm();
    setUpdating(false);
    handleShowModal();
  };

  const handleRowClickFiscal = (id: string) => {
    setFiscalId(id);
    setUpdating(true);
    setShowModal(true);

    const itemSelecionado = data?.find((d) => d.id === id);
    if (itemSelecionado) {
      const formatDateLocal = (iso: string) => iso?.split('T')[0];
      setFormFiscal({
        id: itemSelecionado.id,
        fiscalId: Number(itemSelecionado.fiscal.id),
        tipoFiscal: itemSelecionado.tipoFiscal,
        dataInicio: itemSelecionado.dataInicio
          ? formatDateLocal(itemSelecionado.dataInicio)
          : '',
        dataTermino: itemSelecionado.dataTermino
          ? formatDateLocal(itemSelecionado.dataTermino)
          : '',
        status: itemSelecionado.status === 'S' ? '1' : '2',
        documentoSei: itemSelecionado.documentoSei,
        link: itemSelecionado.link,
        observacoes: itemSelecionado.observacoes,
        usuarioId: userID,
        tabId: parseInt(id_tab, 10),
      });

      setLogData({
        id: itemSelecionado.id,
        usuarioRegistro: itemSelecionado.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao:
          itemSelecionado.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: itemSelecionado.dataRegistro,
        dataAtualizacao: itemSelecionado.dataUltimaAtualizacao,
        nEdicoes: itemSelecionado.nEdicoes,
      });
    }
  };

  //////FUNÇÕES CRUD
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    // Campos que devem ser convertidos para número
    const camposNumericos = ['tipoFiscal', 'idUsuarioFiscal'];

    setFormFiscal((prev: FormFiscalType) => ({
      ...prev,
      [name]: camposNumericos.includes(name) ? parseInt(value, 10) : value,
    }));
  };

  const handleCreate = async () => {
    try {
      const finalDataTermino = dataTerminoRef.current?.triggerBlur();
      const updatedForm = {
        ...formFiscal,
        dataInicio: formFiscal.dataInicio ? formFiscal.dataInicio : null,
        dataTermino: finalDataTermino ? formFiscal.dataTermino : null,
      };

      if (!validateForm(updatedForm)) throw new Error('Formulário inválido.');

      const formToSend = {
        ...updatedForm,
        dataInicio: formFiscal.dataInicio || null,
        fiscalId: Number(formFiscal.fiscalId),
        status: Number(formFiscal.status) === 1 ? 'S' : 'N',
      };

      await httpClient.post(`/api/fiscalaquisicao?tab=${tab}`, formToSend);
      swalSave();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao salvar registro:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      const finalDataTermino = dataTerminoRef.current?.triggerBlur();
      const updatedForm = {
        ...formFiscal,
        dataInicio: formFiscal.dataInicio ? formFiscal.dataInicio : null,
        dataTermino: finalDataTermino ? formFiscal.dataTermino : null,
      };

      if (!validateForm(updatedForm)) throw new Error('Formulário inválido.');

      const formToSend = {
        ...updatedForm,
        dataInicio: formFiscal.dataInicio || null,
        fiscalId: Number(formFiscal.fiscalId),
        status: Number(formFiscal.status) === 1 ? 'S' : 'N',
      };

      const response = await httpClient.patch(
        `/api/fiscalaquisicao/${fiscalId}`,
        formToSend,
      );
      swalAtualizacao('Sucesso', response.data.message);
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao atualizar registro:', err);
    }
  };

  const handleDelete = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      await httpClient.delete(`/api/fiscalaquisicao/${fiscalId}`);
      swalDeleteConfirm();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao excluir registro:', err);
    }
  };

  //////OUTRAS FUNÇÕES
  const validateForm = (form: any) => {
    const erros = [];
    if (!form.status) erros.push('<b>Status</b> é obrigatório.');
    if (!form.tipoFiscal)
      erros.push('<b>Tipo de Fiscalização</b> é obrigatório.');
    if (!form.dataInicio) erros.push('<b>Data de Início</b> é obrigatório.');

    if (Number(form.status) === 2) {
      if (!form.dataTermino)
        erros.push('<b>Data de Término</b> é obrigatório.');
    }
    if (!isUrlValid) erros.push('<b>Link</b> é inválido.');
    if (Number(form.idUsuarioFiscal) < 1)
      erros.push('<b>Fiscal</b> é obrigatório.');

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }
    return true;
  };

  return (
    <BoxConteudo boxTitulo="Fiscais do Instrumento Aquisitivo" fechado>
      <CustomRow>
        <CustomCol width="100%">
          <Tabela
            headers={headersFiscalContrato}
            dataTable={tableData}
            displayValues={valoresExibidosFiscalContrato}
            onRowClick={handleRowClickFiscal}
            botaoAbrirOffCanvas={false}
            noOffCanvasTable
            showPagination={false}
            mostrarBotaoCopiar={false}
          />
        </CustomCol>
      </CustomRow>
      <CustomCol className="d-flex justify-content-end">
        <CustomButton
          label="Novo Registro"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          onClick={() => {
            setUpdating(false);
            handleClickInserirNovo();
          }}
          //   disabled={fichaID == 'undefined' ? true : false}
        />
      </CustomCol>

      {/* Modal Fiscal /*/}
      <CustomOffcanvas
        title="Fiscal do Contrato"
        show={showModal}
        onClose={handleShowModal}
        width="70vw"
      >
        <WorkspaceFicha>
          <Log
            {...logData}
            entity="TB_FISCAL_AQUISICAO"
            id={Number(formFiscal.id)}
          />
          <BoxConteudo boxTitulo="Fiscal">
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="200px">
                <Select
                  name="status"
                  value={formFiscal.status}
                  label="Status"
                  options={statusOption}
                  onChange={handleChange}
                  style={statusFiscalStyle}
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <Select
                  name="tipoFiscal"
                  value={formFiscal.tipoFiscal}
                  label="Tipo de Fiscalização"
                  options={tipoFiscalOptions}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="220px">
                <CustomInputDate
                  name="dataInicio"
                  title="Data Início"
                  value={formFiscal.dataInicio}
                  required
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol width="220px">
                <CustomInputDate
                  name="dataTermino"
                  title="Data Término"
                  value={formFiscal.dataTermino}
                  required={!statusFiscal}
                  onChange={handleChange}
                  readOnly={statusFiscal}
                  ref={dataTerminoRef}
                  minDate={
                    formFiscal.dataInicio
                      ? new Date(formFiscal.dataInicio)
                      : undefined
                  }
                />
              </CustomCol>
              <CustomCol width="220px">
                <CustomInputText
                  name="documentoSei"
                  title="Documento SEI"
                  value={formFiscal.documentoSei}
                  onChange={handleChange}
                  placeholder="Documento SEI"
                  type="text"
                />
              </CustomCol>
              <CustomCol></CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputLink
                  name="link"
                  value={formFiscal.link}
                  title="Link"
                  onChange={handleChange}
                  showActions
                  onValidationChange={setIsUrlValid}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <Select
                  name="fiscalId"
                  value={formFiscal.fiscalId}
                  label="Fiscal"
                  options={usuariosOptions}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow style={{ marginTop: '10px' }}>
              <Col>
                <CustomInputText
                  name="observacoes"
                  title="Observações"
                  value={formFiscal.observacoes}
                  onChange={handleChange}
                  placeholder="Documento SEI"
                  type="textArea"
                  rows={5}
                />
              </Col>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                // deleteDisabled={btDisable}
                onClickDelete={handleDelete}
                save
                // saveDisabled={btDisable}
                onClickSave={handleUpdate}
              />
            ) : (
              <WorkspaceBotoes
                inserir
                // inserirDisabled={btDisable}
                onClickInserir={handleCreate}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
};

export default FiscalContratoComponente;
